import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, FileSpreadsheet, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { baixarBlob, copiarTexto } from './rotina';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Lista de cobrança de uma transportadora (Adnéia, 17/10/2026, decisão 5): as entregas
// em "Ag. Transportadora - <grupo>" com o rastreio de cada uma, prontas para copiar no
// e-mail ou baixar em Excel. O ELO só monta a lista: quem manda é o atendente.
// A lista vai para a transportadora (Adnéia, 19/10/2026): só entrega, nota e rastreio.
const ListaCobranca = ({ grupo, onClose }) => {
  const { getAuthHeader } = useAuth();
  const [linhas, setLinhas] = useState(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (!grupo) return;
    setLinhas(null); setErro('');
    axios.get(`${API_URL}/api/relatorios/acionar-transportadora`, { params: { grupo }, headers: getAuthHeader() })
      .then(r => setLinhas(r.data.linhas || []))
      .catch(e => setErro(e?.response?.data?.detail || 'Não carregou a lista. Tente de novo.'));
  }, [grupo, getAuthHeader]);

  const copiar = () => {
    const head = ['Entrega', 'Nota', 'Rastreio'].join('\t');
    const body = (linhas || []).map(l => [l.entrega, l.nota, l.rastreio].join('\t')).join('\n');
    copiarTexto(head + '\n' + body,
      () => toast.success(`${linhas.length} entrega(s) copiada(s)`),
      () => toast.error('Não copiou. Selecione a tabela e copie com Ctrl+C.'));
  };
  const baixar = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/relatorios/acionar-transportadora-xlsx`,
        { params: { grupo }, headers: getAuthHeader(), responseType: 'blob' });
      baixarBlob(r.data, `Cobranca ${grupo.replace('&', 'e')} ${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch { toast.error('Não gerou o Excel. Tente de novo.'); }
  };

  return (
    <Dialog open={!!grupo} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cobrar a {grupo}</DialogTitle>
          <DialogDescription>
            Entregas em Ag. Transportadora - {grupo}, das mais paradas para as mais novas, com o rastreio.
            Copie para o e-mail ou baixe o Excel. Quem manda é você.
          </DialogDescription>
        </DialogHeader>
        {erro && <p className="text-sm text-red-600">{erro}</p>}
        {!erro && linhas === null && (
          <p className="text-sm text-muted-foreground flex items-center gap-2"><RefreshCw className="h-4 w-4 animate-spin" /> Montando a lista…</p>
        )}
        {linhas && linhas.length === 0 && <p className="text-sm text-muted-foreground">Nenhuma entrega neste status agora.</p>}
        {linhas && linhas.length > 0 && (
          <>
            <div className="flex gap-2">
              <Button variant="outline" onClick={copiar}><Copy className="h-4 w-4 mr-2" />Copiar ({linhas.length})</Button>
              <Button variant="outline" onClick={baixar}><FileSpreadsheet className="h-4 w-4 mr-2" />Baixar Excel</Button>
            </div>
            <div className="overflow-x-auto border rounded-md">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800/30 border-b">
                  <tr>
                    {['Entrega', 'Nota', 'Rastreio'].map(h => (
                      <th key={h} className="text-left px-2 py-1.5 font-semibold whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((l, i) => (
                    <tr key={l.entrega + i} className="border-b last:border-0 align-top">
                      <td className="px-2 py-1.5 font-mono">{l.entrega}</td>
                      <td className="px-2 py-1.5 font-mono">{l.nota || '?'}</td>
                      <td className="px-2 py-1.5 font-mono whitespace-nowrap">
                        {l.rastreio || '?'}{l.rastreio_tipo && <span className="ml-1 text-[11px] text-muted-foreground">{l.rastreio_tipo}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ListaCobranca;
